import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export const languages = [
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "it", label: "IT", name: "Italiano" },
  { code: "en", label: "EN", name: "English" },
] as const;

export type LanguageCode = (typeof languages)[number]["code"];

const languageStorageKey = "begner-language";

type HeaderLink = {
  to: "/" | "/auftragnehmer" | "/auftraege" | "/preise" | "/immobilien" | "/artikel";
  hash?: string;
  label: string;
};

type FooterLink = {
  to:
    | "/"
    | "/agb"
    | "/artikel"
    | "/anbieter-preise"
    | "/preise"
    | "/immobilien"
    | "/mehrfamilienhaus-verkaufen"
    | "/datenschutz"
    | "/impressum"
    | "/auftrag-einstellen"
    | "/auftraege"
    | "/auftragnehmer"
    | "/leistungen"
    | "/makler"
    | "/registrieren";
  hash?: string;
  action?: "cookie-settings";
  search?: { kategorie: "auftragnehmer" | "makler" | "inserate" } | { typ: "immobilienmakler" };
  label: string;
};

type FooterColumn = {
  title: string;
  links: FooterLink[];
};

type Translation = {
  header: {
    navItems: HeaderLink[];
    searchLabel: string;
    login: string;
    account: string;
    logout: string;
    verified: string;
    inReview: string;
    postJob: string;
    menuTitle: string;
    openMenu: string;
  };
  footer: {
    description: string;
    columns: FooterColumn[];
    languageLabel: string;
    copyright: string;
  };
};

export const translations = {
  de: {
    header: {
      navItems: [
        { to: "/auftragnehmer", label: "Handwerkerverzeichnis" },
        { to: "/auftraege", label: "Aufträge suchen" },
        { to: "/preise", label: "Kostenrechner" },
        { to: "/immobilien", label: "Immobilien inserieren" },
        { to: "/", hash: "ueber-uns", label: "Über uns" },
        { to: "/artikel", label: "Magazin" },
      ],
      searchLabel: "Suche",
      login: "Login",
      account: "Mein Konto",
      logout: "Abmelden",
      verified: "Geprüft",
      inReview: "In Prüfung",
      postJob: "Anfrage eingeben",
      menuTitle: "Menü",
      openMenu: "Menü öffnen",
    },
    footer: {
      description:
        "Der einfachste Weg, in der Schweiz geprüfte Handwerker und Auftragnehmer zu finden.",
      columns: [
        {
          title: "Begner",
          links: [
            { to: "/", hash: "ueber-uns", label: "Über uns" },
            { to: "/preise", label: "Kostenrechner" },
            { to: "/immobilien", label: "Immobilien inserieren" },
            { to: "/mehrfamilienhaus-verkaufen", label: "Diskreter Immobilienverkauf" },
            { to: "/artikel", label: "Magazin" },
            { to: "/impressum", hash: "kontakt", label: "Kontakt" },
          ],
        },
        {
          title: "Für Kunden",
          links: [
            { to: "/auftrag-einstellen", label: "Anfrage eingeben" },
            { to: "/auftragnehmer", label: "Handwerkerverzeichnis" },
            { to: "/leistungen", label: "Leistungen nach Kanton" },
            { to: "/makler", label: "Maklerverzeichnis" },
            { to: "/preise", label: "Richtpreise" },
            { to: "/artikel", label: "Ratgeber" },
          ],
        },
        {
          title: "Für Anbieter",
          links: [
            { to: "/registrieren", label: "Profil erstellen" },
            { to: "/auftraege", label: "Aktuelle Aufträge" },
            {
              to: "/anbieter-preise",
              label: "Preise für Auftragnehmer",
            },
            {
              to: "/anbieter-preise",
              search: { kategorie: "makler" },
              label: "Preise für Makler",
            },
            {
              to: "/registrieren",
              search: { typ: "immobilienmakler" },
              label: "Makler werden",
            },
          ],
        },
        {
          title: "Rechtliches",
          links: [
            { to: "/agb", label: "AGB" },
            { to: "/datenschutz", label: "Datenschutz" },
            { to: "/impressum", label: "Impressum" },
            { to: "/impressum", hash: "kontakt", label: "Support" },
            { to: "/", action: "cookie-settings", label: "Cookie-Einstellungen" },
          ],
        },
      ],
      languageLabel: "Sprache:",
      copyright: "Alle Rechte vorbehalten.",
    },
  },
  fr: {
    header: {
      navItems: [
        { to: "/auftragnehmer", label: "Prestataires" },
        { to: "/auftraege", label: "Mandats" },
        { to: "/preise", label: "Prix" },
        { to: "/immobilien", label: "Immobilier" },
        { to: "/", hash: "ueber-uns", label: "À propos" },
        { to: "/artikel", label: "Magazine" },
      ],
      searchLabel: "Recherche",
      login: "Connexion",
      account: "Mon compte",
      logout: "Se déconnecter",
      verified: "Vérifié",
      inReview: "En vérification",
      postJob: "Publier un mandat",
      menuTitle: "Menu",
      openMenu: "Ouvrir le menu",
    },
    footer: {
      description:
        "Le moyen le plus simple de trouver des artisans et prestataires vérifiés en Suisse.",
      columns: [
        {
          title: "Begner",
          links: [
            { to: "/", hash: "ueber-uns", label: "À propos" },
            { to: "/preise", label: "Calculateur de coûts" },
            { to: "/immobilien", label: "Immobilier" },
            { to: "/mehrfamilienhaus-verkaufen", label: "Diskreter Immobilienverkauf (DE)" },
            { to: "/artikel", label: "Magazine" },
            { to: "/impressum", hash: "kontakt", label: "Contact" },
          ],
        },
        {
          title: "Pour les clients",
          links: [
            { to: "/auftrag-einstellen", label: "Publier un mandat" },
            { to: "/auftragnehmer", label: "Trouver des prestataires" },
            { to: "/leistungen", label: "Prestations par canton (DE)" },
            { to: "/makler", label: "Annuaire des agents (DE)" },
            { to: "/preise", label: "Prix indicatifs" },
            { to: "/artikel", label: "Guides" },
          ],
        },
        {
          title: "Pour les professionnels",
          links: [
            { to: "/registrieren", label: "Créer un profil" },
            { to: "/auftraege", label: "Mandats actuels" },
            {
              to: "/anbieter-preise",
              label: "Prix pour les prestataires",
            },
            {
              to: "/anbieter-preise",
              search: { kategorie: "makler" },
              label: "Prix pour les courtiers",
            },
            {
              to: "/registrieren",
              search: { typ: "immobilienmakler" },
              label: "Devenir courtier",
            },
          ],
        },
        {
          title: "Juridique",
          links: [
            { to: "/agb", label: "Conditions générales" },
            { to: "/datenschutz", label: "Protection des données" },
            { to: "/impressum", label: "Mentions légales" },
            { to: "/impressum", hash: "kontakt", label: "Assistance" },
            { to: "/", action: "cookie-settings", label: "Paramètres des cookies" },
          ],
        },
      ],
      languageLabel: "Langue:",
      copyright: "Tous droits réservés.",
    },
  },
  it: {
    header: {
      navItems: [
        { to: "/auftragnehmer", label: "Imprese" },
        { to: "/auftraege", label: "Incarichi" },
        { to: "/preise", label: "Prezzi" },
        { to: "/immobilien", label: "Immobili" },
        { to: "/", hash: "ueber-uns", label: "Chi siamo" },
        { to: "/artikel", label: "Rivista" },
      ],
      searchLabel: "Cerca",
      login: "Accedi",
      account: "Il mio account",
      logout: "Esci",
      verified: "Verificato",
      inReview: "In verifica",
      postJob: "Pubblica un incarico",
      menuTitle: "Menu",
      openMenu: "Apri menu",
    },
    footer: {
      description: "Il modo più semplice per trovare artigiani e imprese verificate in Svizzera.",
      columns: [
        {
          title: "Begner",
          links: [
            { to: "/", hash: "ueber-uns", label: "Chi siamo" },
            { to: "/preise", label: "Calcolatore costi" },
            { to: "/immobilien", label: "Immobili" },
            { to: "/mehrfamilienhaus-verkaufen", label: "Diskreter Immobilienverkauf (DE)" },
            { to: "/artikel", label: "Rivista" },
            { to: "/impressum", hash: "kontakt", label: "Contatto" },
          ],
        },
        {
          title: "Per clienti",
          links: [
            { to: "/auftrag-einstellen", label: "Pubblica un incarico" },
            { to: "/auftragnehmer", label: "Trova imprese" },
            { to: "/leistungen", label: "Servizi per cantone (DE)" },
            { to: "/makler", label: "Elenco agenti (DE)" },
            { to: "/preise", label: "Prezzi indicativi" },
            { to: "/artikel", label: "Guide" },
          ],
        },
        {
          title: "Per professionisti",
          links: [
            { to: "/registrieren", label: "Crea profilo" },
            { to: "/auftraege", label: "Incarichi attuali" },
            {
              to: "/anbieter-preise",
              label: "Prezzi per imprese",
            },
            {
              to: "/anbieter-preise",
              search: { kategorie: "makler" },
              label: "Prezzi per agenti",
            },
            {
              to: "/registrieren",
              search: { typ: "immobilienmakler" },
              label: "Diventa agente",
            },
          ],
        },
        {
          title: "Legale",
          links: [
            { to: "/agb", label: "CG" },
            { to: "/datenschutz", label: "Protezione dati" },
            { to: "/impressum", label: "Colophon" },
            { to: "/impressum", hash: "kontakt", label: "Supporto" },
            { to: "/", action: "cookie-settings", label: "Impostazioni cookie" },
          ],
        },
      ],
      languageLabel: "Lingua:",
      copyright: "Tutti i diritti riservati.",
    },
  },
  en: {
    header: {
      navItems: [
        { to: "/auftragnehmer", label: "Contractors" },
        { to: "/auftraege", label: "Jobs" },
        { to: "/preise", label: "Cost calculator" },
        { to: "/immobilien", label: "Real estate" },
        { to: "/", hash: "ueber-uns", label: "About us" },
        { to: "/artikel", label: "Magazine" },
      ],
      searchLabel: "Search",
      login: "Log in",
      account: "My account",
      logout: "Log out",
      verified: "Verified",
      inReview: "Under review",
      postJob: "Post a job",
      menuTitle: "Menu",
      openMenu: "Open menu",
    },
    footer: {
      description: "The easiest way to find verified tradespeople and contractors in Switzerland.",
      columns: [
        {
          title: "Begner",
          links: [
            { to: "/", hash: "ueber-uns", label: "About us" },
            { to: "/preise", label: "Cost calculator" },
            { to: "/immobilien", label: "Real estate" },
            { to: "/mehrfamilienhaus-verkaufen", label: "Diskreter Immobilienverkauf (DE)" },
            { to: "/artikel", label: "Magazine" },
            { to: "/impressum", hash: "kontakt", label: "Contact" },
          ],
        },
        {
          title: "For customers",
          links: [
            { to: "/auftrag-einstellen", label: "Post a job" },
            { to: "/auftragnehmer", label: "Find contractors" },
            { to: "/leistungen", label: "Services by canton (DE)" },
            { to: "/makler", label: "Agent directory (DE)" },
            { to: "/preise", label: "Guide prices" },
            { to: "/artikel", label: "Guides" },
          ],
        },
        {
          title: "For professionals",
          links: [
            { to: "/registrieren", label: "Create a profile" },
            { to: "/auftraege", label: "Current jobs" },
            {
              to: "/anbieter-preise",
              label: "Contractor pricing",
            },
            {
              to: "/anbieter-preise",
              search: { kategorie: "makler" },
              label: "Agent pricing",
            },
            {
              to: "/registrieren",
              search: { typ: "immobilienmakler" },
              label: "Become an agent",
            },
          ],
        },
        {
          title: "Legal",
          links: [
            { to: "/agb", label: "Terms" },
            { to: "/datenschutz", label: "Privacy" },
            { to: "/impressum", label: "Legal notice" },
            { to: "/impressum", hash: "kontakt", label: "Support" },
            { to: "/", action: "cookie-settings", label: "Cookie settings" },
          ],
        },
      ],
      languageLabel: "Language:",
      copyright: "All rights reserved.",
    },
  },
} satisfies Record<LanguageCode, Translation>;

type LanguageContextValue = {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: Translation;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function isLanguageCode(value: string | null): value is LanguageCode {
  return languages.some((language) => language.code === value);
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>("de");

  useEffect(() => {
    const storedLanguage = window.localStorage.getItem(languageStorageKey);

    if (isLanguageCode(storedLanguage)) {
      setLanguageState(storedLanguage);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem(languageStorageKey, language);
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage: setLanguageState,
      t: translations[language],
    }),
    [language],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }

  return context;
}
